import { useCallback } from 'react';

/* eslint-disable import/order -- CI lints `-api` before `/` (correct ASCII), local eslint --fix reverses it; disable to keep CI happy. */
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
/* eslint-enable import/order */

import { useSpaceStoreActions } from '../../controllers/space-store/hooks';
import type { SpaceSourceResponse } from '../../controllers/space-store/types';

import { addSpaceSourceFetchAction } from './useAddSpaceSource.fetch';

type UseAddSpaceSourceProps = {
	spaceId: string;
	cloudId: string;
	product?: string;
	experienceId?: string;
};

type AddSourceParams = {
	/** The URL of the source. */
	url: string;
	/** The resolved ARI of the source. Null or undefined falls back to the URL. */
	ari?: string | null;
	/** Human-readable display name. */
	name?: string | null;
	/** The source type (e.g. 'confluence', 'jira'). */
	sourceType?: string;
};

type AddSourceCallbacks = {
	onSuccess?: (data: SpaceSourceResponse) => void;
	onError?: (error: ApiError) => void;
};

type UseAddSpaceSourceResult = {
	addSource: (
		params: AddSourceParams,
		callbacks?: AddSourceCallbacks,
	) => Promise<SpaceSourceResponse | undefined>;
	isAdding: boolean;
	error: ApiError | null;
};

/**
 * Adds a source to a space. Wraps the shared `useMutation` so that:
 *  - in-flight requests are cancelled on unmount (or when the user submits a
 *    fresh source before the previous one resolves)
 *  - `error` and `isAdding` come from a single canonical reducer instead of
 *    bespoke `useState` flags duplicated across hooks
 *  - the spaces sweet-state store is mirrored on success so any open
 *    ManageSpaceModal / side-nav row reflects the new source immediately.
 *
 * Public API (`addSource(params)`, `isAdding`, `error`) is unchanged from the
 * previous manual implementation.
 */
export const useAddSpaceSource = ({
	spaceId,
	cloudId,
	product = 'rovo',
	experienceId = 'ai-mate',
}: UseAddSpaceSourceProps): UseAddSpaceSourceResult => {
	const { upsertSpaceSource } = useSpaceStoreActions();

	const { mutate, isSubmitting, error } = useMutation<AddSourceParams, SpaceSourceResponse>({
		mutationFn: async ({ url, ari, name, sourceType }) =>
			addSpaceSourceFetchAction({
				spaceId,
				request: { ari: ari ?? url, url, name, sourceType },
				cloudId,
				product,
				experienceId,
			}),
		onSuccess: (response) => {
			// Mirror to the store so any consumer of `useSpaceById` /
			// `useSpaceDetail` sees the new source without a refetch.
			upsertSpaceSource(spaceId, response);
		},
	});

	const addSource = useCallback(
		(params: AddSourceParams, callbacks?: AddSourceCallbacks) => mutate(params, callbacks),
		[mutate],
	);

	return { addSource, isAdding: isSubmitting, error };
};
